import {
  ACTIVITY_STATUSES,
  CONTROL_PLANE_SKILL_PLAN_STATES,
  CONTROL_PLANE_CONNECTION_STATES,
  CONTROL_PLANE_EXPIRY_BANDS,
  ONBOARDING_PUBLIC_ERROR_CODES,
  PHASES_ORDER,
  WORKSPACE_CLEANUP_STATES,
  isSessionObservabilityProjection,
  type ControlPlaneCapability,
  type ControlPlaneManagementResponse,
  type ControlPlaneSkillPlanProjection,
  type OnboardingPublicError,
} from '@internal/spassu-code-contract';

import {
  object,
  hasOnlyKeys,
  stringOrNull,
  timestampOrNull,
  validTimestamp,
  type JsonObject,
} from './controlPlaneDecoderValues';
import { attempt } from './controlPlaneAttemptDecoder';

export { decodeResumeOnboardingAttemptResponse } from './controlPlaneAttemptDecoder';

function capability(value: unknown): value is ControlPlaneCapability {
  const row = object(value);
  return Boolean(
    row &&
      typeof row.allowed === 'boolean' &&
      stringOrNull(row.reasonCode) &&
      stringOrNull(row.reason),
  );
}

export function decodeControlPlaneSkillPlanProjection(
  value: unknown,
): ControlPlaneSkillPlanProjection {
  const row = object(value);
  if (
    !row ||
    !hasOnlyKeys(row, ['phase', 'state', 'required', 'optionalCount']) ||
    !PHASES_ORDER.includes(row.phase as never) ||
    !CONTROL_PLANE_SKILL_PLAN_STATES.includes(row.state as never) ||
    !Array.isArray(row.required) ||
    !Number.isInteger(row.optionalCount) ||
    Number(row.optionalCount) < 0
  ) {
    throw new Error('A resposta de skills da atividade é inválida.');
  }
  const validItems = row.required.every(itemValue => {
    const item = object(itemValue);
    return Boolean(
      item &&
        hasOnlyKeys(item, [
          'name',
          'description',
          'canonicalAlias',
          'version',
          'origin',
          'scopeType',
        ]) &&
        typeof item.name === 'string' &&
        stringOrNull(item.description) &&
        stringOrNull(item.canonicalAlias) &&
        typeof item.version === 'string' &&
        ['internal', 'community'].includes(String(item.origin)) &&
        ['corporate', 'project'].includes(String(item.scopeType)),
    );
  });
  if (!validItems) {
    throw new Error('A resposta de skills da atividade é inválida.');
  }
  return value as ControlPlaneSkillPlanProjection;
}

function capabilities(value: unknown): boolean {
  const row = object(value);
  if (!row) return false;
  const required = [
    'pair',
    'renew',
    'recover',
    'resume',
    'openCockpit',
    'finalize',
    'cancel',
    'selectForDeletion',
    'overrideCleanup',
    'enrollCleanup',
  ];
  return (
    required.every(key => capability(row[key])) &&
    (row.recoverUsage === undefined || capability(row.recoverUsage))
  );
}

function connectionProjection(value: unknown): boolean {
  const connection = object(value);
  return Boolean(
    connection &&
      CONTROL_PLANE_CONNECTION_STATES.some(
        state => state === connection.state,
      ) &&
      CONTROL_PLANE_EXPIRY_BANDS.some(band => band === connection.expiryBand) &&
      timestampOrNull(connection.expiresAt) &&
      (connection.remainingMs === null ||
        typeof connection.remainingMs === 'number') &&
      timestampOrNull(connection.lastConfirmedAt) &&
      stringOrNull(connection.sessionId) &&
      stringOrNull(connection.credentialId),
  );
}

function projectionMetadata(row: JsonObject): boolean {
  return (
    typeof row.activityId === 'string' &&
    ACTIVITY_STATUSES.some(status => status === row.activityCycle) &&
    validTimestamp(row.observedAt) &&
    typeof row.stale === 'boolean'
  );
}

function workspaceProjection(value: unknown): boolean {
  const workspace = object(value);
  return Boolean(
    workspace &&
      WORKSPACE_CLEANUP_STATES.some(state => state === workspace.state),
  );
}

function optionalSessionObservability(value: unknown): boolean {
  return value === undefined || isSessionObservabilityProjection(value);
}

function projection(value: unknown): boolean {
  const row = object(value);
  return Boolean(
    row &&
      projectionMetadata(row) &&
      connectionProjection(row.connection) &&
      attempt(row.attempt) &&
      workspaceProjection(row.workspace) &&
      capabilities(row.capabilities) &&
      optionalSessionObservability(row.sessionObservability),
  );
}

export function decodeControlPlaneManagementResponse(
  value: unknown,
): ControlPlaneManagementResponse {
  const row = object(value);
  const activity = object(row?.activity);
  if (
    !row ||
    !activity ||
    typeof activity.id !== 'string' ||
    typeof activity.title !== 'string' ||
    activity.workPath !== 'control-plane' ||
    !projection(row.projection)
  ) {
    throw new Error('A resposta de gestão da conexão é inválida.');
  }
  return value as ControlPlaneManagementResponse;
}

export function decodeOnboardingPublicError(
  value: unknown,
): Partial<OnboardingPublicError> | undefined {
  const row = object(value);
  if (!row) return undefined;
  const keys = Object.keys(row);
  if (
    !keys.every(key => ['code', 'message', 'action'].includes(key)) ||
    !ONBOARDING_PUBLIC_ERROR_CODES.some(code => code === row.code) ||
    typeof row.message !== 'string' ||
    (row.action !== undefined && typeof row.action !== 'string')
  ) {
    return undefined;
  }
  return row as unknown as Partial<OnboardingPublicError>;
}
